import React, { useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Button, Checkbox, Input, Radio } from 'components/UIElements'

const CREATE_NEW_SELECTION = '__create_new__'

const formatInstanceDateTime = value => {
  if (!value || !moment(value).isValid()) return null
  return moment(value).format('MMM D, YYYY h:mm A')
}

/** Keep datetime-local as naive local ISO (same as Create DCF) — no UTC shift. */
const toApiDateTime = value => {
  if (!value) return null
  const raw = String(value).trim()
  if (!raw) return null
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return `${raw}:00`
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(raw)) return raw
  if (!moment(raw).isValid()) return null
  return moment(raw).format('YYYY-MM-DDTHH:mm:ss')
}

const isInstanceCompleted = candidate => {
  const status = String(candidate?.status || '').toLowerCase()
  if (status === 'completed') return true
  if (status === 'pending') return false
  return Boolean(candidate?.completedBy)
}

const actionForSelection = (selectedId, instances, dcfAction) => {
  // Flag-only DCFs only link an instance; never rewrite the action.
  if (dcfAction === 'flag_questions_only') return 'flag_questions_only'
  if (selectedId === CREATE_NEW_SELECTION) return 'create_new_record'
  const candidate = (instances || []).find(item => item.id === selectedId)
  return isInstanceCompleted(candidate) ? 'add_data_to_existing_record' : 'add_new_record'
}

const formatFlag = value => (value ? 'Yes' : 'No')

/** Client-side filter on instance start_date (day precision). */
const matchesStartDateFilter = (candidate, startFrom, startTo) => {
  if (!startFrom && !startTo) return true
  if (!candidate?.startedBy || !moment(candidate.startedBy).isValid()) return false
  const started = moment(candidate.startedBy)
  if (startFrom && started.isBefore(moment(startFrom), 'day')) return false
  if (startTo && started.isAfter(moment(startTo), 'day')) return false
  return true
}

/**
 * Drawer to list instrument instances and associate (or replace) one with the
 * current DCF. For most actions, selection drives action:
 * - completed instance → Add data to existing record
 * - incomplete instance → Add new record
 * - create new → Create new record (instance id minted on Apply)
 * Flag Questions Only keeps that action and lets the user set
 * transfer_ignore / reporting_ignore for apply.
 */
const AssociateInstanceDrawer = ({
  studyID,
  dcfId,
  participantId,
  instrumentId,
  currentInstanceId = null,
  dcfAction = null,
  subjectLabel = null,
  instrumentLabel = null,
  searchDcfInstances,
  associateDcfInstance,
  fetchDcfList,
  closeDrawer,
}) => {
  const [paperFrom, setPaperFrom] = useState('')
  const [paperTo, setPaperTo] = useState('')
  const [startFilterFrom, setStartFilterFrom] = useState('')
  const [startFilterTo, setStartFilterTo] = useState('')
  const [hasLoaded, setHasLoaded] = useState(false)
  const [instances, setInstances] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [transferIgnore, setTransferIgnore] = useState(false)
  const [reportingIgnore, setReportingIgnore] = useState(false)
  const isFlagOnly = dcfAction === 'flag_questions_only'

  const rangeInvalid = Boolean(paperFrom && paperTo && paperFrom > paperTo)
  const startFilterInvalid = Boolean(
    startFilterFrom && startFilterTo && startFilterFrom > startFilterTo,
  )
  const filteredInstances = instances.filter(candidate =>
    matchesStartDateFilter(candidate, startFilterFrom, startFilterTo),
  )
  const createNewSelected = selectedId === CREATE_NEW_SELECTION
  const selectedAction = selectedId ? actionForSelection(selectedId, instances, dcfAction) : null
  const canLoad = Boolean(studyID && participantId && instrumentId)
  const canConfirm =
    Boolean(selectedId) &&
    !saving &&
    !startFilterInvalid &&
    (!createNewSelected || (paperFrom && paperTo && !rangeInvalid))

  const onStartFilterChange = (field, value) => {
    if (field === 'from') setStartFilterFrom(value)
    else setStartFilterTo(value)
    // Clear instance selection if it would fall outside the new filter.
    if (selectedId && selectedId !== CREATE_NEW_SELECTION) {
      const candidate = instances.find(item => item.id === selectedId)
      const nextFrom = field === 'from' ? value : startFilterFrom
      const nextTo = field === 'to' ? value : startFilterTo
      if (!matchesStartDateFilter(candidate, nextFrom, nextTo)) {
        setSelectedId(null)
      }
    }
  }

  const onLoadInstances = () => {
    if (!canLoad || !searchDcfInstances) return
    if (participantId == null || instrumentId == null) {
      setErrorText('Missing participant or instrument for this DCF. Close and try again.')
      return
    }
    setErrorText('')
    setHasLoaded(true)
    setSelectedId(null)
    setInstances([])
    setStartFilterFrom('')
    setStartFilterTo('')
    setTransferIgnore(false)
    setReportingIgnore(false)
    searchDcfInstances(studyID, participantId, instrumentId).then(list => {
      if (!Array.isArray(list)) {
        setErrorText('Could not load instances. Check that DCF services are running.')
        setInstances([])
        return
      }
      setInstances(list)
    })
  }

  const onConfirm = async () => {
    if (!selectedId || !associateDcfInstance || !selectedAction) return
    setErrorText('')

    if (
      currentInstanceId &&
      !createNewSelected &&
      String(currentInstanceId) !== String(selectedId)
    ) {
      const ok = window.confirm(
        'This DCF is already linked to an instance. Replace it with the selected one?',
      )
      if (!ok) return
    }
    if (currentInstanceId && createNewSelected) {
      const ok = window.confirm(
        'This DCF is already linked to an instance. Clear that link and switch to Create New Record (instance created on Apply)?',
      )
      if (!ok) return
    }

    setSaving(true)
    try {
      let patch
      if (createNewSelected) {
        patch = {
          instanceId: null,
          action: 'create_new_record',
          startBy: toApiDateTime(paperFrom),
          completedBy: toApiDateTime(paperTo),
        }
      } else {
        const candidate = instances.find(item => item.id === selectedId)
        patch = {
          instanceId: selectedId,
          action: isFlagOnly ? 'flag_questions_only' : selectedAction,
          startBy: candidate?.startedBy ? toApiDateTime(candidate.startedBy) : undefined,
          completedBy: candidate?.completedBy ? toApiDateTime(candidate.completedBy) : null,
        }
        if (isFlagOnly) {
          patch.transferIgnore = transferIgnore
          patch.reportingIgnore = reportingIgnore
        }
      }

      const detail = await associateDcfInstance(studyID, dcfId, patch)
      if (!detail) {
        throw new Error('Could not associate an instance. Please try again.')
      }
      if (fetchDcfList) fetchDcfList(studyID, { showLoader: false })
      closeDrawer()
    } catch (err) {
      setErrorText(err?.message || 'Could not associate an instance. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const confirmLabel = () => {
    if (createNewSelected) return 'Use create new record'
    if (selectedAction === 'add_new_record') {
      return currentInstanceId ? 'Replace as add new record' : 'Associate as add new record'
    }
    if (selectedAction === 'add_data_to_existing_record') {
      return currentInstanceId ? 'Replace as add to existing' : 'Associate as add to existing'
    }
    if (isFlagOnly) {
      return currentInstanceId ? 'Replace instance and flags' : 'Associate instance and flags'
    }
    return currentInstanceId ? 'Replace instance' : 'Associate instance'
  }

  return (
    <div className='associate-instance-drawer'>
      <div className='associate-meta'>
        <div className='meta-item'>
          <span className='meta-label'>Subject</span>
          <strong>{subjectLabel || participantId || '—'}</strong>
        </div>
        <div className='meta-item'>
          <span className='meta-label'>Instrument</span>
          <strong>{instrumentLabel || instrumentId || '—'}</strong>
        </div>
        <div className='meta-item'>
          <span className='meta-label'>Current instance</span>
          <strong className={currentInstanceId ? '' : 'meta-muted'}>
            {currentInstanceId || 'Not linked'}
          </strong>
        </div>
        {isFlagOnly && (
          <div className='meta-item'>
            <span className='meta-label'>Action</span>
            <strong>Flag Questions Only</strong>
          </div>
        )}
      </div>

      <section className='associate-search-section'>
        <h4 className='section-title'>Instrument instances</h4>
        <p className='helper-text'>
          {isFlagOnly
            ? 'Load instances for this participant and instrument. Select the instance whose transfer_ignore and reporting_ignore flags should be updated. The DCF action stays Flag Questions Only.'
            : 'Load all database instances for this participant and instrument. Selecting a completed instance sets action to Add data to existing record; an incomplete instance sets Add new record. Choose create new below if none apply.'}
        </p>
        <div className='search-action-row'>
          <Button
            id='associate-search-instances'
            content={hasLoaded ? 'Reload instances' : 'Load instances'}
            disabled={!canLoad}
            onClick={onLoadInstances}
          />
        </div>
      </section>

      {hasLoaded && (
        <section className='instance-candidates'>
          <h4 className='section-title'>Instances</h4>
          {instances.length > 0 && (
            <div className='instance-start-filter'>
              <p className='helper-text'>Filter by start date to narrow a long list.</p>
              <div className='paper-range-fields'>
                <div className='date-field'>
                  <label className='field-label' htmlFor='associate-start-from'>
                    Started from
                  </label>
                  <Input
                    id='associate-start-from'
                    type='date'
                    value={startFilterFrom}
                    onChange={value => onStartFilterChange('from', value)}
                  />
                </div>
                <div className='date-field'>
                  <label className='field-label' htmlFor='associate-start-to'>
                    Started to
                  </label>
                  <Input
                    id='associate-start-to'
                    type='date'
                    value={startFilterTo}
                    onChange={value => onStartFilterChange('to', value)}
                  />
                </div>
              </div>
              {startFilterInvalid && (
                <div className='range-error'>Started from must be earlier than or equal to Started to.</div>
              )}
              {!startFilterInvalid && (startFilterFrom || startFilterTo) && (
                <p className='filter-result-count'>
                  Showing {filteredInstances.length} of {instances.length} instances
                </p>
              )}
            </div>
          )}
          {instances.length === 0 ? (
            <div className='no-instances'>No instances found for this participant and instrument.</div>
          ) : filteredInstances.length === 0 ? (
            <div className='no-instances'>No instances match this start date filter.</div>
          ) : (
            <ul className='instance-candidate-list'>
              {filteredInstances.map(candidate => {
                const startedLabel = formatInstanceDateTime(candidate.startedBy)
                const completedLabel = formatInstanceDateTime(candidate.completedBy)
                const completed = isInstanceCompleted(candidate)
                const isSelected = selectedId === candidate.id
                return (
                  <li key={candidate.id}>
                    <button
                      type='button'
                      className={`instance-candidate-card${isSelected ? ' selected' : ''}`}
                      onClick={() => {
                        setSelectedId(candidate.id)
                        if (isFlagOnly) {
                          setTransferIgnore(Boolean(candidate.transferIgnore))
                          setReportingIgnore(Boolean(candidate.reportingIgnore))
                        }
                      }}
                    >
                      <Radio justCircle selected={isSelected} onClick={() => { }} />
                      <div className='instance-candidate-meta'>
                        <div className='instance-candidate-line'>
                          <span className='meta-label'>Status</span>
                          <strong className={`instance-status ${completed ? 'completed' : 'pending'}`}>
                            {completed ? 'Completed' : 'Not completed'}
                          </strong>
                        </div>
                        <div className='instance-candidate-line'>
                          <span className='meta-label'>Start Date</span>
                          <strong>{startedLabel || '—'}</strong>
                        </div>
                        <div className='instance-candidate-line'>
                          <span className='meta-label'>Completed</span>
                          <strong>{completedLabel || '—'}</strong>
                        </div>
                        {isFlagOnly ? (
                          <>
                            <div className='instance-candidate-line'>
                              <span className='meta-label'>Transfer ignore</span>
                              <strong>{formatFlag(candidate.transferIgnore)}</strong>
                            </div>
                            <div className='instance-candidate-line'>
                              <span className='meta-label'>Reporting ignore</span>
                              <strong>{formatFlag(candidate.reportingIgnore)}</strong>
                            </div>
                          </>
                        ) : (
                          <div className='instance-candidate-line'>
                            <span className='meta-label'>Action</span>
                            <strong>
                              {completed ? 'Add data to existing record' : 'Add new record'}
                            </strong>
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          {isFlagOnly && selectedId && !createNewSelected && (
            <div className='flag-editor'>
              <h4 className='section-title'>Reporting flags for apply</h4>
              <p className='helper-text'>
                These values are saved on the DCF and written to instruments_instances for the
                selected instance when the DCF is applied.
              </p>
              <Checkbox
                id='associate-transfer-ignore'
                checked={transferIgnore}
                label='Transfer ignore'
                onClick={() => setTransferIgnore(!transferIgnore)}
              />
              <Checkbox
                id='associate-reporting-ignore'
                checked={reportingIgnore}
                label='Reporting ignore'
                onClick={() => setReportingIgnore(!reportingIgnore)}
              />
            </div>
          )}

          {!isFlagOnly && (
            <>
              <button
                type='button'
                className={`instance-candidate-card create-new-card${createNewSelected ? ' selected' : ''
                  }`}
                onClick={() => setSelectedId(CREATE_NEW_SELECTION)}
              >
                <Radio justCircle selected={createNewSelected} onClick={() => { }} />
                <div className='instance-candidate-meta'>
                  <strong>None of these — create a new instance</strong>
                  <p className='create-new-hint'>
                    Sets action to Create New Record. Enter the paper form dates below. The database
                    instance id is created when the DCF is applied.
                  </p>
                </div>
              </button>

              {createNewSelected && (
                <div className='paper-range-fields create-new-dates'>
                  <div className='date-field'>
                    <label className='field-label' htmlFor='associate-paper-from'>
                      Paper From
                    </label>
                    <Input
                      id='associate-paper-from'
                      type='datetime-local'
                      value={paperFrom}
                      onChange={value => setPaperFrom(value)}
                    />
                  </div>
                  <div className='date-field'>
                    <label className='field-label' htmlFor='associate-paper-to'>
                      Paper To
                    </label>
                    <Input
                      id='associate-paper-to'
                      type='datetime-local'
                      value={paperTo}
                      onChange={value => setPaperTo(value)}
                    />
                  </div>
                  {rangeInvalid && (
                    <div className='range-error'>From must be earlier than or equal to To.</div>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {errorText && (
        <div className='associate-error'>
          <i className='fas fa-exclamation-circle' /> {errorText}
        </div>
      )}

      <div className='button-list flexed end-justified associate-actions'>
        <Button grey type='button' content='Cancel' onClick={closeDrawer} disabled={saving} />
        <Button
          type='button'
          content={confirmLabel()}
          disabled={!canConfirm}
          onClick={onConfirm}
        />
      </div>
    </div>
  )
}

AssociateInstanceDrawer.propTypes = {
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dcfId: PropTypes.string,
  participantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  instrumentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentInstanceId: PropTypes.string,
  dcfAction: PropTypes.string,
  subjectLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  instrumentLabel: PropTypes.string,
  searchDcfInstances: PropTypes.func,
  associateDcfInstance: PropTypes.func,
  fetchDcfList: PropTypes.func,
  closeDrawer: PropTypes.func,
}

export default AssociateInstanceDrawer
